/**
 * SprintAssignment 値オブジェクト
 *
 * スプリントと成果物（PBI）の紐付け情報を表す値オブジェクト。
 * 同一スプリント内で複数担当者を設定可能にするため、
 * 各紐付けは一意のUUIDを持つ。
 */

import { UserBasic } from '../../../lib/functions/user'

/**
 * スプリント紐付け情報の詳細
 * (SprintProductItemDetail と同等)
 */
export interface SprintProductItem {
  /** 紐付けの一意ID（同一スプリント内で複数担当者を区別） */
  uuid: string
  /** 紐付け先のスプリントUUID */
  sprintUuid: string
  /** 見積時間 */
  estimatedHour: number
  /** タスク見積時間 */
  taskEstimatedHour: number
  /** 表示順 */
  displayOrder: number
  /** ロックバージョン（楽観的ロック用） */
  lockVersion: number
  /** PBI担当者（Sprint×PBI単位） */
  assignee?: UserBasic | null
  /** スプリントゴール（Sprint×PBI単位） */
  sprintGoal?: string | null
}

// ============================================================================
// 判定関数（純粋関数）
// ============================================================================

/**
 * 指定スプリントに紐付いているか判定（成果物単位）
 */
export const isAssignedToSprint = (
  assignments: SprintProductItem[],
  sprintUuid: string
): boolean => assignments.some(a => a.sprintUuid === sprintUuid)

/**
 * 複数スプリントに紐付いているか判定
 */
export const isAssignedToMultipleSprints = (
  assignments: SprintProductItem[]
): boolean => {
  const uniqueSprints = new Set(assignments.map(a => a.sprintUuid))
  return uniqueSprints.size > 1
}

/**
 * 何らかのスプリントに紐付いているか判定
 */
export const isAssignedToAnySprint = (
  assignments: SprintProductItem[]
): boolean => assignments.length > 0

/**
 * 行単位で指定スプリントに紐付いているか判定
 * 共有配列でも正しく動作する（成果物単位ではなく行単位で判定）
 *
 * @param assignments スプリント紐付け配列
 * @param rowUuid 行のUUID（= SprintAssignmentのUUID）
 * @param sprintUuid 判定対象のスプリントUUID
 */
export const isRowAssignedToSprint = (
  assignments: SprintProductItem[],
  rowUuid: string,
  sprintUuid: string
): boolean => {
  const rowAssignment = assignments.find(a => a.uuid === rowUuid)
  return rowAssignment?.sprintUuid === sprintUuid
}

/**
 * 紐付いているスプリントUUIDのリストを取得
 */
export const getAssignedSprintUuids = (
  assignments: SprintProductItem[]
): string[] => assignments.map(a => a.sprintUuid)

/**
 * 特定のUUIDを持つ紐付け情報を取得
 */
export const findAssignmentByUuid = (
  assignments: SprintProductItem[],
  uuid: string
): SprintProductItem | undefined => assignments.find(a => a.uuid === uuid)

/**
 * 特定のスプリントUUIDを持つ紐付け情報を取得
 */
export const findAssignmentBySprintUuid = (
  assignments: SprintProductItem[],
  sprintUuid: string
): SprintProductItem | undefined =>
  assignments.find(a => a.sprintUuid === sprintUuid)
